import {AuthSnapshot, login, register, resetPassword, sendRegisterCode, sendResetCode} from './auth.js';
import {BRAND_AUTH_FOOTER, BRAND_NAME, BRAND_PRODUCT} from './brand.js';
import {escapeHtml, renderLayout} from './layout.js';
import {navigate} from './router.js';

/**
 * 统一认证页面入口（登录、注册、找回密码）
 * @param root 页面挂载根节点
 * @param mode 模式：login / register / reset
 * @param destination 跳转目标：user (普通用户) / admin (管理员控制台)
 */
export function renderAuth(root: HTMLElement, mode: 'login' | 'register' | 'reset', destination: 'user' | 'admin' = 'user'): void {
  if (mode === 'login') {
    root.innerHTML = renderLoginPage(destination === 'admin');
    bindLoginPage(root, destination);
    return;
  }
  if (mode === 'register') {
    root.innerHTML = renderRegisterPage();
    bindRegisterPage(root);
    return;
  }
  root.innerHTML = renderResetPage();
  bindResetPage(root);
}

/**
 * 渲染科技极简风格登录页
 * @param isAdmin 是否为管理员登录模式
 */
function renderLoginPage(isAdmin: boolean): string {
  const heading = isAdmin ? '管理员登录' : '欢迎登录工作台';
  const subheading = isAdmin ? '原力引擎 · 管理控制台' : '让复杂的办公工作，在智能引擎中自动流转';
  const submitText = isAdmin ? '登录控制台' : '进入工作台';

  return `<div class="oai-auth-page">
    <div class="oai-ambient-glow" aria-hidden="true"></div>

    <header class="oai-auth-topbar">
      <a class="oai-auth-brand" href="/" aria-label="${escapeHtml(BRAND_NAME)} 首页">
        <div class="oai-brand-mark">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M2 17L12 22L22 17" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M2 12L12 17L22 12" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <span class="oai-auth-brand-name">${escapeHtml(BRAND_NAME)}</span>
      </a>
      ${isAdmin ? '' : `<a class="oai-auth-switch-link" href="/register">没有账号？免费注册 <span>→</span></a>`}
    </header>

    <main class="oai-auth-main">
      <form class="oai-auth-card" id="authForm" novalidate autocomplete="on">
        <div class="oai-auth-badge">
          <span class="oai-badge-dot"></span>
          <span>${escapeHtml(BRAND_NAME)} · ${escapeHtml(BRAND_PRODUCT)}</span>
        </div>

        <h1 class="oai-auth-title">${heading}</h1>
        <p class="oai-auth-sub">${subheading}</p>

        <div class="oai-field" data-field="email">
          <label class="oai-field-label" for="email">登录邮箱</label>
          <div class="oai-input-wrap">
            <span class="oai-input-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <rect x="2" y="4" width="20" height="16" rx="3"/>
                <path d="M22 6L12 13L2 6"/>
              </svg>
            </span>
            <input type="email" id="email" name="email" placeholder="name@company.com" autocomplete="username" required>
          </div>
          <div class="oai-field-error">请输入正确的邮箱格式</div>
        </div>

        <div class="oai-field" data-field="password">
          <label class="oai-field-label" for="password">密码</label>
          <div class="oai-input-wrap">
            <span class="oai-input-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </span>
            <input type="password" id="password" name="password" placeholder="请输入您的密码" autocomplete="current-password" required>
            <button type="button" class="oai-pwd-toggle" data-toggle-target="password" aria-label="显示或隐藏密码" tabindex="-1">
              <svg class="icon-eye-off" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              <svg class="icon-eye-on" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" hidden><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            </button>
          </div>
          <div class="oai-field-error">${isAdmin ? '请输入管理员密码' : '请输入密码'}</div>
        </div>

        <div class="oai-auth-helpers">
          <label class="oai-remember">
            <input type="checkbox" name="remember" value="1">
            <span>记住此设备</span>
          </label>
          ${isAdmin ? '<a href="https://www.xiaostorm.com/login" class="oai-auth-link">员工登录</a>' : '<a href="/forgot-password" class="oai-auth-link">忘记密码？</a>'}
        </div>

        <button type="submit" class="oai-auth-submit" id="loginBtn">
          <span class="btn-text">${submitText}</span>
          <span class="btn-spinner" aria-hidden="true"></span>
        </button>

        <div class="oai-form-message" id="formMessage" role="alert" aria-live="polite"></div>

        ${isAdmin ? '' : `<div class="oai-auth-bottom-links oai-auth-bottom-center">
          <span>还没有账号？</span>
          <a href="/register" class="oai-auth-link-bold">立即注册新账号 →</a>
        </div>`}

        <footer class="oai-auth-footer">
          <p>${escapeHtml(BRAND_AUTH_FOOTER)}</p>
        </footer>
      </form>
    </main>
  </div>`;
}

/**
 * 渲染科技极简风格注册页
 */
function renderRegisterPage(): string {
  return `<div class="oai-auth-page">
    <div class="oai-ambient-glow" aria-hidden="true"></div>

    <header class="oai-auth-topbar">
      <a class="oai-auth-brand" href="/" aria-label="${escapeHtml(BRAND_NAME)} 首页">
        <div class="oai-brand-mark">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M2 17L12 22L22 17" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M2 12L12 17L22 12" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <span class="oai-auth-brand-name">${escapeHtml(BRAND_NAME)}</span>
      </a>
      <a class="oai-auth-switch-link" href="/login">已有账号？直接登录 <span>→</span></a>
    </header>

    <main class="oai-auth-main">
      <form class="oai-auth-card oai-auth-card--register" id="authForm" novalidate autocomplete="off">
        <div class="oai-auth-badge">
          <span class="oai-badge-dot"></span>
          <span>开启智能办公新体验</span>
        </div>

        <h1 class="oai-auth-title">创建新账号</h1>
        <p class="oai-auth-sub">几秒内完成创建，即可拥有专属智能工作台与团队空间</p>

        <div class="oai-field-grid">
          <div class="oai-field" data-field="displayName">
            <label class="oai-field-label" for="displayName">姓名</label>
            <div class="oai-input-wrap">
              <span class="oai-input-icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
              </span>
              <input type="text" id="displayName" name="displayName" placeholder="您的称呼" autocomplete="off" autocapitalize="words" spellcheck="false" required>
            </div>
            <div class="oai-field-error">请输入姓名</div>
          </div>

          <div class="oai-field" data-field="phone">
            <label class="oai-field-label" for="phone">手机号</label>
            <div class="oai-input-wrap">
              <span class="oai-input-icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/>
                </svg>
              </span>
              <input type="tel" id="phone" name="phone" placeholder="11 位手机号码" autocomplete="off" inputmode="tel" maxlength="11" required>
            </div>
            <div class="oai-field-error">请输入正确的 11 位手机号</div>
          </div>
        </div>

        <div class="oai-field" data-field="email">
          <label class="oai-field-label" for="email">工作邮箱</label>
          <div class="oai-input-wrap">
            <span class="oai-input-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <rect x="2" y="4" width="20" height="16" rx="3"/><path d="M22 6L12 13L2 6"/>
              </svg>
            </span>
            <input type="email" id="email" name="email" placeholder="name@company.com" autocomplete="off" required>
          </div>
          <div class="oai-field-error">请输入有效的工作邮箱</div>
        </div>

        <div class="oai-field" data-field="password">
          <label class="oai-field-label" for="password">设置密码</label>
          <div class="oai-input-wrap">
            <span class="oai-input-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </span>
            <input type="password" id="password" name="password" placeholder="至少 8 位字符，建议包含数字与字母" minlength="8" autocomplete="new-password" required>
            <button type="button" class="oai-pwd-toggle" data-toggle-target="password" aria-label="显示或隐藏密码" tabindex="-1">
              <svg class="icon-eye-off" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              <svg class="icon-eye-on" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" hidden><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            </button>
          </div>
          <div class="oai-field-error">密码长度至少 8 位</div>
        </div>

        <div class="oai-field" data-field="code">
          <label class="oai-field-label" for="code">邮箱验证码</label>
          <div class="oai-code-row">
            <div class="oai-input-wrap">
              <span class="oai-input-icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </span>
              <input type="text" id="code" name="code" placeholder="6 位验证码" inputmode="numeric" maxlength="8" autocomplete="one-time-code" required>
            </div>
            <button type="button" id="sendCode" class="oai-code-btn">获取验证码</button>
          </div>
          <div class="oai-field-error">请输入邮箱收到的验证码</div>
        </div>

        <button type="submit" class="oai-auth-submit" id="registerBtn">
          <span class="btn-text">立即注册账号</span>
          <span class="btn-spinner" aria-hidden="true"></span>
        </button>

        <div class="oai-form-message" id="formMessage" role="alert" aria-live="polite"></div>

        <div class="oai-auth-bottom-links">
          <a href="/login" class="oai-auth-link">已有账号？返回登录</a>
          <a href="/forgot-password" class="oai-auth-link">找回密码</a>
        </div>

        <footer class="oai-auth-footer">
          <p>${escapeHtml(BRAND_AUTH_FOOTER)}</p>
        </footer>
      </form>
    </main>
  </div>`;
}

/**
 * 渲染科技极简风格找回密码页
 */
function renderResetPage(): string {
  return `<div class="oai-auth-page">
    <div class="oai-ambient-glow" aria-hidden="true"></div>

    <header class="oai-auth-topbar">
      <a class="oai-auth-brand" href="/" aria-label="${escapeHtml(BRAND_NAME)} 首页">
        <div class="oai-brand-mark">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M2 17L12 22L22 17" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M2 12L12 17L22 12" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <span class="oai-auth-brand-name">${escapeHtml(BRAND_NAME)}</span>
      </a>
      <a class="oai-auth-switch-link" href="/login">想起密码？返回登录 <span>→</span></a>
    </header>

    <main class="oai-auth-main">
      <form class="oai-auth-card" id="authForm" novalidate autocomplete="on">
        <div class="oai-auth-badge">
          <span class="oai-badge-dot"></span>
          <span>安全凭证重置</span>
        </div>

        <h1 class="oai-auth-title">重置登录密码</h1>
        <p class="oai-auth-sub">验证绑定的企业工作邮箱后，即可设定新登录密码</p>

        <div class="oai-field" data-field="email">
          <label class="oai-field-label" for="email">绑定邮箱</label>
          <div class="oai-input-wrap">
            <span class="oai-input-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <rect x="2" y="4" width="20" height="16" rx="3"/><path d="M22 6L12 13L2 6"/>
              </svg>
            </span>
            <input type="email" id="email" name="email" placeholder="name@company.com" autocomplete="email" required>
          </div>
          <div class="oai-field-error">请输入绑定邮箱</div>
        </div>

        <div class="oai-field" data-field="code">
          <label class="oai-field-label" for="code">邮箱验证码</label>
          <div class="oai-code-row">
            <div class="oai-input-wrap">
              <span class="oai-input-icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </span>
              <input type="text" id="code" name="code" placeholder="请输入验证码" inputmode="numeric" required>
            </div>
            <button type="button" id="sendCode" class="oai-code-btn">获取验证码</button>
          </div>
          <div class="oai-field-error">请输入验证码</div>
        </div>

        <div class="oai-field" data-field="password">
          <label class="oai-field-label" for="password">设定新密码</label>
          <div class="oai-input-wrap">
            <span class="oai-input-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </span>
            <input type="password" id="password" name="password" placeholder="至少 8 位新密码" minlength="8" autocomplete="new-password" required>
            <button type="button" class="oai-pwd-toggle" data-toggle-target="password" aria-label="显示或隐藏密码" tabindex="-1">
              <svg class="icon-eye-off" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              <svg class="icon-eye-on" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" hidden><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            </button>
          </div>
          <div class="oai-field-error">密码长度至少 8 位</div>
        </div>

        <button type="submit" class="oai-auth-submit" id="resetBtn">
          <span class="btn-text">确认并重置密码</span>
          <span class="btn-spinner" aria-hidden="true"></span>
        </button>

        <div class="oai-form-message" id="formMessage" role="alert" aria-live="polite"></div>

        <div class="oai-auth-bottom-links">
          <a href="/login" class="oai-auth-link">返回登录</a>
          <a href="/register" class="oai-auth-link">注册新账号</a>
        </div>

        <footer class="oai-auth-footer">
          <p>${escapeHtml(BRAND_AUTH_FOOTER)}</p>
        </footer>
      </form>
    </main>
  </div>`;
}

/**
 * 挂载密码显隐小眼睛切换通用逻辑
 * @param root 容器
 */
function bindPasswordToggles(root: HTMLElement): void {
  root.querySelectorAll<HTMLButtonElement>('.oai-pwd-toggle').forEach((btn) => {
    btn.querySelectorAll<SVGElement>('svg[hidden]').forEach(icon=>{icon.style.display='none';});
    btn.addEventListener('click', () => {
      const targetId = btn.dataset.toggleTarget;
      if (!targetId) return;
      const input = root.querySelector<HTMLInputElement>(`#${targetId}`);
      if (!input) return;

      const isPwd = input.type === 'password';
      input.type = isPwd ? 'text' : 'password';

      const eyeOff = btn.querySelector<SVGElement>('.icon-eye-off');
      const eyeOn = btn.querySelector<SVGElement>('.icon-eye-on');
      if (eyeOff && eyeOn) {
        eyeOff.toggleAttribute('hidden',isPwd);
        eyeOn.toggleAttribute('hidden',!isPwd);
        eyeOff.style.display = isPwd ? 'none' : '';
        eyeOn.style.display = isPwd ? '' : 'none';
      }
    });
  });
}

/**
 * 绑定通用倒计时逻辑
 * @param button 发送按钮
 * @param onSend 执行发送的异步操作
 */
function bindCountdownSend(button: HTMLButtonElement, onSend: () => Promise<void>): void {
  let timer: number | null = null;
  button.addEventListener('click', async () => {
    if (button.disabled) return;
    try {
      await onSend();
      let left = 60;
      button.disabled = true;
      button.textContent = `${left} 秒后重发`;
      timer = window.setInterval(() => {
        left--;
        if (left <= 0) {
          if (timer) clearInterval(timer);
          button.disabled = false;
          button.textContent = '获取验证码';
        } else {
          button.textContent = `${left} 秒后重发`;
        }
      }, 1000);
    } catch {
      button.disabled = false;
      button.textContent = '获取验证码';
    }
  });
}

/**
 * 绑定登录逻辑
 */
function bindLoginPage(root: HTMLElement, destination: 'user' | 'admin'): void {
  bindPasswordToggles(root);

  const form = root.querySelector<HTMLFormElement>('#authForm');
  const message = root.querySelector<HTMLElement>('#formMessage');
  const btn = root.querySelector<HTMLButtonElement>('#loginBtn');
  const emailInput = root.querySelector<HTMLInputElement>('#email');
  const passInput = root.querySelector<HTMLInputElement>('#password');
  const rememberInput = root.querySelector<HTMLInputElement>('input[name="remember"]');
  const emailField = form?.querySelector<HTMLElement>('[data-field="email"]');
  const passField = form?.querySelector<HTMLElement>('[data-field="password"]');

  const emailValid = (v: string) => /^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(v);
  const setInvalid = (field: HTMLElement | null | undefined, on: boolean) => {
    if (!field) return;
    on ? field.classList.add('is-invalid') : field.classList.remove('is-invalid');
  };

  const rememberedEmailKey = `tax-assistant:${destination}:remembered-email`;
  try {
    const rememberedEmail = window.localStorage.getItem(rememberedEmailKey) ?? '';
    if (rememberedEmail && emailInput) emailInput.value = rememberedEmail;
    if (rememberedEmail && rememberInput) rememberInput.checked = true;
  } catch {
    // 本地存储不可用时不影响正常运行
  }

  emailInput?.addEventListener('blur', () => setInvalid(emailField, !emailValid(emailInput.value)));
  emailInput?.addEventListener('input', () => setInvalid(emailField, false));
  passInput?.addEventListener('blur', () => setInvalid(passField, !passInput.value));
  passInput?.addEventListener('input', () => setInvalid(passField, false));

  form?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const email = emailInput?.value.trim() ?? '';
    const password = passInput?.value ?? '';

    if (!emailValid(email)) {
      setInvalid(emailField, true);
      emailInput?.focus();
      return;
    }
    if (!password) {
      setInvalid(passField, true);
      passInput?.focus();
      return;
    }

    btn?.classList.add('is-loading');
    if (btn) btn.disabled = true;
    if (message) message.textContent = '';

    try {
      const rememberMe = Boolean(rememberInput?.checked);
      const auth = await login(email, password, rememberMe);
      try {
        if (rememberMe) window.localStorage.setItem(rememberedEmailKey, email);
        else window.localStorage.removeItem(rememberedEmailKey);
      } catch {
        // 无需中断
      }

      if (!auth?.user?.id || !auth.organization?.id || !auth.organization?.name) {
        throw new Error('登录状态缺少团队空间信息，请稍后重试');
      }

      if (destination === 'admin') {
        if (!['super_admin', 'operator'].includes(auth.role)) {
          if (message) message.textContent = '该账号没有管理员权限';
          btn?.classList.remove('is-loading');
          if (btn) btn.disabled = false;
          return;
        }
        navigate('/admin');
        return;
      }

      navigate('/tax-assistant/tasks');
    } catch (error) {
      btn?.classList.remove('is-loading');
      if (btn) btn.disabled = false;
      const rawMessage = error instanceof Error ? error.message : String(error);
      if (message) {
        message.textContent = rawMessage.includes('邮箱或密码错误') || rawMessage.includes('401')
          ? '邮箱未注册或密码错误，请检查后重试'
          : rawMessage.includes('禁用') || rawMessage.includes('inactive')
            ? '该账号已被禁用，请联系管理员'
            : rawMessage;
      }
    }
  });
}

/**
 * 绑定注册逻辑
 */
/**
 * 公开注册表单：抑制浏览器在本机弹出曾填写过的姓名/手机号（非服务端数据）。
 */
function bindRegisterAutofillGuard(root: HTMLElement): void {
  for (const selector of ['#displayName', '#phone', '#email']) {
    const input = root.querySelector<HTMLInputElement>(selector);
    if (!input) continue;
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('readonly', 'readonly');
    const unlock = () => input.removeAttribute('readonly');
    input.addEventListener('focus', unlock, {once: true});
    input.addEventListener('pointerdown', unlock, {once: true});
  }
}

function bindRegisterPage(root: HTMLElement): void {
  bindPasswordToggles(root);
  bindRegisterAutofillGuard(root);

  const form = root.querySelector<HTMLFormElement>('#authForm');
  const message = root.querySelector<HTMLElement>('#formMessage');
  const button = root.querySelector<HTMLButtonElement>('#registerBtn');
  const sendCodeBtn = root.querySelector<HTMLButtonElement>('#sendCode');

  if (sendCodeBtn) {
    bindCountdownSend(sendCodeBtn, async () => {
      const email = String(new FormData(form!).get('email') ?? '').trim();
      if (!email || !/^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(email)) {
        if (message) message.textContent = '请先填写正确的接收邮箱';
        const field = form?.querySelector<HTMLElement>('[data-field="email"]');
        field?.classList.add('is-invalid');
        throw new Error('邮箱为空');
      }
      const result = await sendRegisterCode(email);
      if (message) {
        message.textContent = result.devCode
          ? `已发送验证码（开发环境测试码：${result.devCode}）`
          : '验证码已发送至您的邮箱，请注意查收';
      }
    });
  }

  form?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const data = new FormData(form);
    const displayName = String(data.get('displayName') ?? '').trim();
    const phone = String(data.get('phone') ?? '').trim();
    const email = String(data.get('email') ?? '').trim();
    const password = String(data.get('password') ?? '');
    const code = String(data.get('code') ?? '').trim();

    let hasError = false;
    const check = (fieldName: string, valid: boolean) => {
      const field = form.querySelector<HTMLElement>(`[data-field="${fieldName}"]`);
      if (field) {
        valid ? field.classList.remove('is-invalid') : field.classList.add('is-invalid');
      }
      if (!valid) hasError = true;
    };

    check('displayName', Boolean(displayName));
    check('phone', /^1\d{10}$/.test(phone));
    check('email', /^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(email));
    check('password', password.length >= 8);
    check('code', Boolean(code));

    if (hasError) return;

    button?.classList.add('is-loading');
    if (button) button.disabled = true;
    if (message) message.textContent = '';

    try {
      const auth = await register(email, phone, password, displayName, code);
      if (!auth?.user?.id || !auth.organization?.id || !auth.organization?.name) {
        throw new Error('注册成功，但工作空间信息初始化异常');
      }
      navigate('/tax-assistant/tasks');
    } catch (error) {
      button?.classList.remove('is-loading');
      if (button) button.disabled = false;
      if (message) message.textContent = error instanceof Error ? error.message : String(error);
    }
  });
}

/**
 * 绑定重置密码逻辑
 */
function bindResetPage(root: HTMLElement): void {
  bindPasswordToggles(root);

  const form = root.querySelector<HTMLFormElement>('#authForm');
  const message = root.querySelector<HTMLElement>('#formMessage');
  const button = root.querySelector<HTMLButtonElement>('#resetBtn');
  const sendCodeBtn = root.querySelector<HTMLButtonElement>('#sendCode');

  if (sendCodeBtn) {
    bindCountdownSend(sendCodeBtn, async () => {
      const email = String(new FormData(form!).get('email') ?? '').trim();
      if (!email || !/^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(email)) {
        if (message) message.textContent = '请先填写需要找回密码的邮箱';
        const field = form?.querySelector<HTMLElement>('[data-field="email"]');
        field?.classList.add('is-invalid');
        throw new Error('邮箱为空');
      }
      const result = await sendResetCode(email);
      if (message) {
        message.textContent = result.devCode
          ? `已发送验证码（开发环境测试码：${result.devCode}）`
          : '验证码已发送至您的邮箱，请注意查收';
      }
    });
  }

  form?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const data = new FormData(form);
    const email = String(data.get('email') ?? '').trim();
    const code = String(data.get('code') ?? '').trim();
    const password = String(data.get('password') ?? '');

    let hasError = false;
    const check = (fieldName: string, valid: boolean) => {
      const field = form.querySelector<HTMLElement>(`[data-field="${fieldName}"]`);
      if (field) {
        valid ? field.classList.remove('is-invalid') : field.classList.add('is-invalid');
      }
      if (!valid) hasError = true;
    };

    check('email', /^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(email));
    check('code', Boolean(code));
    check('password', password.length >= 8);

    if (hasError) return;

    button?.classList.add('is-loading');
    if (button) button.disabled = true;
    if (message) message.textContent = '';

    try {
      await resetPassword(email, code, password);
      if (message) {
        message.classList.add('is-success');
        message.textContent = '密码重置成功！正在跳转至登录页面…';
      }
      setTimeout(() => {
        navigate('/login');
        renderAuth(root, 'login');
      }, 1200);
    } catch (error) {
      button?.classList.remove('is-loading');
      if (button) button.disabled = false;
      if (message) message.textContent = error instanceof Error ? error.message : String(error);
    }
  });
}

/**
 * 渲染旧版通知提示页
 */
export function renderLegacyNotice(root: HTMLElement, auth: AuthSnapshot): void {
  renderLayout(root, auth, `<div class="page-heading"><span class="eyebrow">${escapeHtml(window.location.pathname)}</span><h1>网站模块迁移中</h1><p>该页面已纳入模块化前端路由。桌面版完整业务页面暂时保持独立运行，网站版将逐模块迁移。</p><button class="primary-button" id="backHome">返回任务管理</button></div>`);
  root.querySelector('#backHome')?.addEventListener('click', () => navigate('/tax-assistant/tasks'));
}
